import {
  ExternalLink,
  Award,
  Building2,
  Users,
  BookOpen,
  Quote,
  Heart,
  Landmark,
} from "lucide-react";

const supporters = [
  {
    icon: Award,
    label: "CSR Partner",
    description: "Strategic partnership for heritage preservation",
  },
  {
    icon: Building2,
    label: "Infrastructure Support",
    description: "Building research facilities",
  },
  {
    icon: BookOpen,
    label: "Knowledge Preservation",
    description: "Digitizing sacred manuscripts",
  },
  {
    icon: Users,
    label: "Community Development",
    description: "Supporting scholars and researchers",
  },
];

export function AdaniFoundation() {
  return (
    <section className="bg-[#FAF7F2] py-16 lg:py-20">
      <div className="container mx-auto px-6">
        {/* Organizational Info - Beautiful Cards */}
        <div className="mx-auto mb-16 max-w-5xl">
          <div className="mb-8 text-center">
            <p
              className="mb-3 text-xs font-semibold uppercase tracking-wider"
              style={{ fontFamily: "Inter, sans-serif", color: "#C9A227" }}
            >
              Our Organization
            </p>
            <h2
              className="text-3xl font-bold md:text-4xl"
              style={{
                fontFamily: "Playfair Display, serif",
                color: "#2D2A26",
              }}
            >
              Rooted in <span className="text-[#C9A227]">Faith</span>, Driven by{" "}
              <span className="text-[#C9A227]">Service</span>
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Founded By Card */}
            <div className="group relative overflow-hidden rounded-2xl border border-[#E5E0D5] bg-white p-6 shadow-lg transition-all duration-300 hover:shadow-xl md:p-8">
              {/* Decorative corner */}
              <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full bg-gradient-to-bl from-[#C9A227]/10 to-transparent" />

              <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#C9A227] to-[#D4A03C] shadow-md">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p
                      className="text-xs font-semibold uppercase tracking-wider text-[#C9A227]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Founded by
                    </p>
                  </div>
                </div>

                <h3
                  className="mb-2 text-xl font-bold text-[#2D2A26]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Srimati Shantaben Shantilal Bhudarmal Adani Family
                </h3>

                <div className="my-4 h-px w-16 bg-[#C9A227]/30" />

                <div className="space-y-1">
                  <p
                    className="text-base font-medium text-[#2D2A26]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Muni Jambuvijayaji Gyankendra
                  </p>
                  <p
                    className="text-sm text-[#5A5650]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    (Muni Jambuvijayaji Research Centre)
                  </p>
                </div>
              </div>
            </div>

            {/* Run By Card */}
            <div className="group relative overflow-hidden rounded-2xl border border-[#E5E0D5] bg-white p-6 shadow-lg transition-all duration-300 hover:shadow-xl md:p-8">
              {/* Decorative corner */}
              <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full bg-gradient-to-bl from-[#C9A227]/10 to-transparent" />

              <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#C9A227] to-[#D4A03C] shadow-md">
                    <Landmark className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p
                      className="text-xs font-semibold uppercase tracking-wider text-[#C9A227]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Run by
                    </p>
                  </div>
                </div>

                <h3
                  className="mb-2 text-xl font-bold text-[#2D2A26]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Sri Siddhi-Bhuvan-Manohar Jain Trust
                </h3>

                <div className="my-4 h-px w-16 bg-[#C9A227]/30" />

                <div className="space-y-1">
                  <p
                    className="text-base font-medium text-[#2D2A26]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Regd. No. 3402
                  </p>
                  <p
                    className="text-sm text-[#5A5650]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Ahmedabad, Gujarat
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Header for Adani */}
        <div className="mb-10 text-center">
          <p
            className="mb-3 text-xs font-semibold uppercase tracking-wider"
            style={{ fontFamily: "Inter, sans-serif", color: "#C9A227" }}
          >
            Our Supporters
          </p>

          <h2
            className="mb-3 text-3xl font-bold md:text-4xl"
            style={{ fontFamily: "Playfair Display, serif", color: "#2D2A26" }}
          >
            Supported by{" "}
            <span className="text-[#C9A227] underline decoration-[#C9A227]/30 underline-offset-4">
              Adani Foundation
            </span>
          </h2>

          <p
            className="mx-auto max-w-2xl text-[15px] leading-relaxed"
            style={{ fontFamily: "Inter, sans-serif", color: "#5A5650" }}
          >
            We are grateful for their generous support in preserving and
            promoting Jain heritage, literature, and sacred knowledge for future
            generations.
          </p>
        </div>

        {/* Main Card */}
        <div className="mx-auto max-w-4xl">
          <div className="rounded-2xl border border-[#E5E0D5] bg-white p-6 shadow-lg md:p-8">
            {/* Logo and Description */}
            <div className="mb-6 flex flex-col items-start gap-6 md:flex-row">
              <div className="flex-shrink-0">
                <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-[#E5E0D5] bg-[#FAF7F2] shadow-sm">
                  <div className="text-center">
                    <span
                      className="block text-xl font-bold text-[#2D2A26]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      ADANI
                    </span>
                    <span
                      className="text-[10px] text-[#5A5650]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      FOUNDATION
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <p
                  className="mb-3 text-sm leading-relaxed text-[#5A5650]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  The Adani Foundation has been instrumental in supporting our
                  mission to digitize, preserve, and share the rich heritage of
                  Jain literature. Their commitment to knowledge preservation
                  aligns perfectly with Gurudev's vision of making ancient
                  wisdom accessible.
                </p>
                <a
                  href="https://www.adanifoundation.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#C9A227] transition-opacity hover:opacity-70"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  Visit Adani Foundation
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Support categories */}
            <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {supporters.map(item => (
                <div
                  key={item.label}
                  className="flex flex-col rounded-lg border border-[#E5E0D5] bg-[#FAF7F2] p-3.5 transition-colors"
                >
                  <div className="mb-2.5 flex h-9 w-9 items-center justify-center rounded-lg bg-[#C9A227]/10">
                    <item.icon className="h-4 w-4 text-[#C9A227]" />
                  </div>
                  <h4
                    className="mb-0.5 text-sm font-semibold text-[#2D2A26]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {item.label}
                  </h4>
                  <p
                    className="text-xs text-[#5A5650]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="rounded-lg border-l-4 border-[#C9A227] bg-[#FAF7F2] p-4">
              <div className="flex gap-3">
                <Quote className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#C9A227]/40" />
                <div>
                  <p
                    className="text-sm italic leading-relaxed text-[#2D2A26]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    "Preserving cultural heritage is preserving our identity. We
                    are honored to support the mission of Jambushrusti in
                    safeguarding the wisdom of Jain philosophy for future
                    generations."
                  </p>
                  <p
                    className="mt-2 text-xs font-medium text-[#8B8B8B]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    — Adani Foundation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
